import { useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts'
import AppDropdown from '../../../components/AppDropdown'
import SharedFilters from '../../../components/SharedFilters'
import { appToast } from '../../../components/AppToast'
import { useExportJob } from '../../../hooks/useExportJob'
import { useAnalyticsListScreen } from './hooks/useAnalyticsListScreen'

const CHART_COLORS = [
  '#0047ab', // Primary Blue
  '#0d9488', // Teal
  '#f59e0b', // Amber
  '#8b5cf6', // Violet
  '#10b981', // Emerald
  '#ec4899', // Pink
  '#f97316', // Orange
  '#06b6d4', // Cyan
  '#6366f1', // Indigo
  '#64748b', // Slate
  '#e11d48', // Rose
  '#84cc16', // Lime
]

function PieTooltip({ active, payload, groupLabel, totalRecords }) {
  if (!active || !payload || !payload.length) return null
  const item = payload[0]
  const val = Number(item.value ?? item.payload?.count ?? 0)
  const categoryName = item.name || item.payload?.name || 'Category'
  const pct = totalRecords > 0 ? ((val / totalRecords) * 100).toFixed(1) : '0.0'
  const color = item.payload?.fill || item.color || '#0047AB'

  return (
    <div className="rounded-xl border border-outline-variant/30 bg-white p-3 shadow-xl text-xs select-none pointer-events-none min-w-[170px]">
      <div className="flex items-center gap-2 pb-1.5 border-b border-outline-variant/15 mb-2">
        <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ backgroundColor: color }} />
        <span className="font-extrabold text-on-surface truncate max-w-[180px]" title={categoryName}>
          {categoryName}
        </span>
      </div>
      <div className="space-y-1.5 text-[11px]">
        <div className="flex items-center justify-between gap-4 text-on-surface-variant font-medium">
          <span>Dimension:</span>
          <span className="font-bold text-on-surface">{groupLabel}</span>
        </div>
        <div className="flex items-center justify-between gap-4 text-on-surface-variant font-medium">
          <span>Enrolled:</span>
          <span className="font-bold text-on-surface tabular-nums">{val.toLocaleString('en-IN')}</span>
        </div>
        <div className="flex items-center justify-between gap-4 text-on-surface-variant font-medium">
          <span>Share:</span>
          <span className="font-extrabold text-primary tabular-nums">{pct}%</span>
        </div>
      </div>
    </div>
  )
}

export default function AnalyticsListScreen() {
  const navigate = useNavigate()
  const { state, actions } = useAnalyticsListScreen()

  useEffect(() => {
    if (state.error) appToast.error(state.error)
  }, [state.error])

  const toOptions = useCallback((arr) => {
    return (arr || []).map((item) =>
      typeof item === 'object' && item !== null
        ? item
        : { value: item, label: item === 'All' ? 'All' : item }
    )
  }, [])

  const { triggerExport, isExporting } = useExportJob({ module: 'admission_analytics' })

  const handleExport = () => {
    triggerExport({
      groupBy: state.groupBy,
      company: state.company,
      branch: state.branch,
      course: state.course,
      batch: state.batch,
      gender: state.gender,
      counsellor: state.counsellor,
      fromDate: state.fromDate,
      toDate: state.toDate,
      tab: state.tab,
    })
  }

  return (
    <div className="mx-auto flex w-full max-w-[1460px] flex-col gap-6 pb-12">
      {/* ── Page Header ───────────────────────────────────────────────────── */}
      <section className="flex flex-col gap-5 border-b border-outline-variant/10 pb-5 xl:flex-row xl:items-end xl:justify-between">
        <div className="max-w-4xl">
          <div className="flex items-center gap-2 text-xs text-on-surface-variant mb-1">
            <span
              onClick={() => navigate('/admission/students/list')}
              className="cursor-pointer hover:text-primary transition-colors font-medium"
            >
              Admission
            </span>
            <span className="material-symbols-outlined text-[10px]">chevron_right</span>
            <span className="font-semibold text-primary">Analytics</span>
          </div>
          <p className="text-xs font-black uppercase tracking-[0.16em] text-primary">Admission Module</p>
          <h1 className="mt-1 font-display text-3xl font-extrabold tracking-tight text-on-surface">
            Admission Analytics
          </h1>
          <p className="mt-1 text-sm text-on-surface-variant">
            Visual admissions reporting, cohort demographics, enrollment distribution, and lead conversions.
          </p>
        </div>

        {/* ── Segmented Tab & Chart Mode Controls ─────────────────────────── */}
        <div className="flex flex-wrap items-center gap-3">
          {/* Status Segmented Tabs */}
          <div className="flex items-center gap-1 rounded-xl bg-surface-container-high p-1">
            {['Active', 'Archive', 'All'].map((tabLabel) => {
              const tabKey = tabLabel.toLowerCase()
              const isActive = state.tab === tabKey
              return (
                <button
                  key={tabKey}
                  type="button"
                  onClick={() => actions.setTab(tabKey)}
                  className={`rounded-lg px-3 py-1.5 text-xs font-bold transition-colors cursor-pointer ${
                    isActive
                      ? 'bg-surface-container-lowest text-primary shadow-sm'
                      : 'text-on-surface-variant hover:text-on-surface'
                  }`}
                >
                  {tabLabel}
                </button>
              )
            })}
          </div>

          {/* Chart Mode Toggle */}
          <div className="flex items-center gap-1 rounded-xl bg-surface-container-high p-1">
            <button
              type="button"
              onClick={() => actions.setChartMode('bar')}
              className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-bold transition-all cursor-pointer ${
                state.chartMode === 'bar'
                  ? 'bg-surface-container-lowest text-primary shadow-sm'
                  : 'text-on-surface-variant hover:text-primary'
              }`}
            >
              <span className="material-symbols-outlined text-sm">bar_chart</span>
              <span>Bar</span>
            </button>
            <button
              type="button"
              onClick={() => actions.setChartMode('pie')}
              className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-bold transition-all cursor-pointer ${
                state.chartMode === 'pie'
                  ? 'bg-surface-container-lowest text-primary shadow-sm'
                  : 'text-on-surface-variant hover:text-primary'
              }`}
            >
              <span className="material-symbols-outlined text-sm">pie_chart</span>
              <span>Pie</span>
            </button>
          </div>
        </div>
      </section>

      {/* ── KPI Summary Cards ──────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {/* Total Records */}
        <div className="flex items-center gap-3.5 rounded-2xl border border-outline-variant/25 bg-surface-container-lowest p-4 shadow-sm transition hover:shadow-md">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
            <span className="material-symbols-outlined text-xl">groups</span>
          </div>
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-on-surface-variant">Total Students</p>
            <p className="mt-0.5 text-lg font-extrabold tracking-tight text-blue-700 tabular-nums">
              {state.totalRecords.toLocaleString('en-IN')}
            </p>
          </div>
        </div>

        {/* Top Segment */}
        <div className="flex items-center gap-3.5 rounded-2xl border border-outline-variant/25 bg-surface-container-lowest p-4 shadow-sm transition hover:shadow-md">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600">
            <span className="material-symbols-outlined text-xl">trending_up</span>
          </div>
          <div className="min-w-0">
            <p className="text-[10px] font-bold uppercase tracking-wider text-on-surface-variant">Top Segment</p>
            <p className="mt-0.5 text-lg font-extrabold tracking-tight text-emerald-700 truncate" title={state.topSegment.name}>
              {state.topSegment.name || '—'}
            </p>
            <p className="text-[11px] text-on-surface-variant font-medium">
              {state.topSegment.count} ({state.topSegment.percentage}%)
            </p>
          </div>
        </div>

        {/* Current Dimension */}
        <div className="flex items-center gap-3.5 rounded-2xl border border-outline-variant/25 bg-surface-container-lowest p-4 shadow-sm transition hover:shadow-md">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-purple-50 text-purple-600">
            <span className="material-symbols-outlined text-xl">category</span>
          </div>
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-on-surface-variant">Active Dimension</p>
            <p className="mt-0.5 text-lg font-extrabold tracking-tight text-purple-700 truncate">
              {state.dimensionLabel}
            </p>
          </div>
        </div>

        {/* Cohort Breakdown */}
        <div className="flex items-center gap-3.5 rounded-2xl border border-outline-variant/25 bg-surface-container-lowest p-4 shadow-sm transition hover:shadow-md">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-amber-50 text-amber-600">
            <span className="material-symbols-outlined text-xl">donut_large</span>
          </div>
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-on-surface-variant">Cohort Breakdown</p>
            <p className="mt-0.5 text-lg font-extrabold tracking-tight text-amber-700 tabular-nums">
              {state.data.length} {state.data.length === 1 ? 'Segment' : 'Segments'}
            </p>
          </div>
        </div>
      </div>

      {/* ── Collapsible Advanced Filters (Shared Component) ────────────────── */}
      <SharedFilters
        showSearchButtons={true}
        showStatus={false}
        companyFilter={state.company}
        setCompanyFilter={actions.setCompany}
        companyOptions={toOptions(state.options.companies)}
        branchFilter={state.branch}
        setBranchFilter={actions.setBranch}
        branchOptions={toOptions(state.options.branches)}
        periodFilter={state.datePeriod}
        setPeriodFilter={actions.handleDatePeriodChange}
        periodOptions={state.options.datePeriodOptions}
        periodLabel="Period"
        fromDate={state.fromDate}
        setFromDate={actions.setFromDate}
        toDate={state.toDate}
        setToDate={actions.setToDate}
        onSearch={actions.search}
        onReset={actions.resetFilters}
        resetLabel="Show All"
        visibleAccordionFilters={[
          'company',
          'branch',
          'period',
          'fromDate',
          'toDate',
        ]}
        tools={[
          { key: 'print', label: 'Print View', icon: 'print' },
          { key: 'export', label: isExporting ? 'Exporting...' : 'Export Excel', icon: 'download' },
        ]}
        onToolSelect={(key) => {
          if (key === 'export') handleExport()
          else if (key === 'print') window.print()
        }}
      />

      {/* ── Dashboard Card (Split-Screen 60/40 Chart & Table) ──────────────── */}
      <section className="overflow-hidden rounded-2xl border border-outline-variant/25 bg-surface-container-lowest shadow-sm">
        {/* Card Title Bar with Dimension Selector */}
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-outline-variant/15 bg-surface-container-low/40 px-6 py-3.5">
          <div className="flex items-center gap-2.5">
            <span className="material-symbols-outlined text-base text-primary">leaderboard</span>
            <span className="text-xs font-bold uppercase tracking-wider text-on-surface">
              Admissions Distribution by {state.dimensionLabel}
            </span>
          </div>

          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <span className="text-xs font-extrabold uppercase tracking-wider text-on-surface-variant">
                Dimension:
              </span>
              <AppDropdown
                value={state.groupBy}
                onChange={(val) => actions.setGroupBy(val)}
                options={state.options.dimensions}
                className="w-56"
                triggerClassName="h-9 px-3 text-xs font-bold text-primary bg-surface-container-lowest border border-outline-variant/30"
              />
            </div>

            <span className="inline-flex items-center rounded-lg bg-primary/10 px-2.5 py-1 text-xs font-bold text-primary">
              {state.data.length} {state.data.length === 1 ? 'segment' : 'segments'}
            </span>
          </div>
        </div>

        {/* Split-Screen 60/40 Container */}
        <div className="grid grid-cols-1 lg:grid-cols-5">
          {/* Left Panel: Chart Container (60%) */}
          <div className="p-6 lg:col-span-3 flex flex-col justify-center">
            {state.isLoading ? (
              <div className="flex h-96 flex-col items-center justify-center text-sm text-on-surface-variant font-medium">
                <span className="material-symbols-outlined animate-spin text-3xl text-primary/40 block mb-2">
                  progress_activity
                </span>
                Loading analytics visualization...
              </div>
            ) : state.data.length === 0 ? (
              <div className="flex h-96 flex-col items-center justify-center text-sm text-on-surface-variant font-medium">
                <span className="material-symbols-outlined text-4xl text-outline mb-2">
                  bar_chart
                </span>
                <p>No student analytics data available for the selected filters.</p>
              </div>
            ) : state.chartMode === 'bar' ? (
              <div className="h-96 w-full min-w-0 min-h-[384px]">
                <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={380}>
                  <BarChart data={state.data} margin={{ top: 15, right: 20, left: 10, bottom: 40 }}>
                    <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="rgba(115, 119, 132, 0.15)" />
                    <XAxis
                      dataKey="name"
                      angle={-25}
                      textAnchor="end"
                      interval={0}
                      height={60}
                      tick={{ fontSize: 11, fill: 'var(--on-surface-variant)', fontWeight: 600 }}
                    />
                    <YAxis
                      allowDecimals={false}
                      tick={{ fontSize: 11, fill: 'var(--on-surface-variant)' }}
                    />
                    <RechartsTooltip
                      cursor={false}
                      contentStyle={{
                        backgroundColor: '#ffffff',
                        border: '1px solid rgba(195, 198, 213, 0.4)',
                        borderRadius: '12px',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                        fontSize: '12px',
                        fontWeight: 'bold',
                      }}
                      formatter={(val) => [`${val} Students`, 'Enrolled']}
                    />
                    <Bar
                      dataKey="count"
                      name="Students"
                      radius={[6, 6, 0, 0]}
                    >
                      {state.data.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-96 w-full min-w-0 min-h-[384px]">
                <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={380}>
                  <PieChart>
                    <Pie
                      data={state.data}
                      dataKey="count"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      innerRadius={65}
                      outerRadius={125}
                      paddingAngle={3}
                      labelLine={false}
                      label={({ name, percent }) => `${(percent * 100).toFixed(0)}%`}
                    >
                      {state.data.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                      ))}
                    </Pie>
                    <RechartsTooltip
                      content={<PieTooltip groupLabel={state.dimensionLabel} totalRecords={state.totalRecords} />}
                    />
                    <Legend wrapperStyle={{ fontSize: 11, paddingTop: '10px' }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          {/* Right Panel: Data Summary Table (40%) */}
          <div className="border-t border-outline-variant/20 lg:col-span-2 lg:border-l lg:border-t-0 flex flex-col justify-between">
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-outline-variant/20 bg-surface-container-low/60 text-xs font-bold uppercase tracking-[0.08em] text-on-surface-variant select-none">
                    <th className="px-5 py-3.5 text-left">{state.dimensionLabel} Name</th>
                    <th className="px-4 py-3.5 text-left w-24">Share</th>
                    <th className="px-5 py-3.5 text-right w-20">Students</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-outline-variant/10">
                  {state.isLoading ? (
                    <tr>
                      <td colSpan={3} className="py-12 text-center text-on-surface-variant font-medium">
                        Loading summary...
                      </td>
                    </tr>
                  ) : state.data.length === 0 ? (
                    <tr>
                      <td colSpan={3} className="py-12 text-center text-on-surface-variant font-medium">
                        No cohorts found
                      </td>
                    </tr>
                  ) : (
                    state.data.map((item, idx) => {
                      const pct = item.percentage ?? (state.totalRecords > 0 ? Math.round((item.count / state.totalRecords) * 100) : 0)
                      const color = CHART_COLORS[idx % CHART_COLORS.length]

                      return (
                        <tr
                          key={item.name || idx}
                          className="transition-colors hover:bg-surface-container-low/40"
                        >
                          <td className="px-5 py-3.5 text-on-surface font-semibold">
                            <div className="flex items-center gap-2">
                              <span
                                className="h-2.5 w-2.5 rounded-full shrink-0"
                                style={{ backgroundColor: color }}
                              />
                              <span className="truncate" title={item.name}>
                                {item.name}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-3.5">
                            <div className="flex items-center gap-2">
                              <div className="h-1.5 w-12 rounded-full bg-surface-container-high overflow-hidden">
                                <div
                                  className="h-full rounded-full"
                                  style={{ width: `${pct}%`, backgroundColor: color }}
                                />
                              </div>
                              <span className="font-mono text-[11px] font-bold text-on-surface-variant">
                                {pct}%
                              </span>
                            </div>
                          </td>
                          <td className="px-5 py-3.5 text-right font-mono font-bold text-on-surface tabular-nums">
                            {Number(item.count).toLocaleString('en-IN')}
                          </td>
                        </tr>
                      )
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Total Footer Summary Bar */}
            {state.data.length > 0 && (
              <div className="flex items-center justify-between border-t border-outline-variant/15 bg-surface-container-low/30 px-5 py-3 text-xs font-bold text-on-surface">
                <span>Total Enrolled</span>
                <span className="font-mono tabular-nums text-primary font-black">
                  {state.totalRecords.toLocaleString('en-IN')}
                </span>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  )
}